import React, { useEffect, useState } from 'react';

import { generateGenericPlaceholder } from '~/src/dash/components/forms/formBuilder/common';
import { useFormBuilderStore } from '~/src/dash/components/forms/formBuilder/contexts';
import { LogicToggle } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/LogicToggle';
import { TextInputWithInclude } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/TextInputWithInclude';
import { Col, Cols, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';
import { ELogicalOperator } from '~/src/dash/utils/options';

const rowHeight = 32;
const labelWidth = 100;
const AppBundleTooltipMessage = (
  <div>
    App Bundle ID to bid on
    <br />- Android: com.hulu.plus
    <br />- Apple TV: 482066631
    <br />- Roku: 12
    <br />- Fire TV: B004U7BXBO
    <br />- Samsung: G15115002089
    <br /> More Info{' '}
    <a
      href='https://iabtechlab.com/wp-content/uploads/2020/08/IAB-Tech-Lab-OTT-store-assigned-App-Identification-Guidelines-2020.pdf'
      rel='noopener'
      target='_blank'
    >
      Here
    </a>
  </div>
);
const SitePagesTooltipMessage = 'Site Pages to bid on. Can be comma-separated.';
const OrTooltipMessage =
  'Enable OR logic between App Bundle and Site Pages; requests will reach (bidder/line item) if any of the entered IDs and site pages match.';
const AndTooltipMessage =
  'Enable AND logic between App Bundle and Site Pages; requests will reach (bidder/line item) only if all of the entered IDs and site pages match.';

export interface IAppBundleIDsSitePagesEditorProps {
  appBundleIDKey: string;
  appBundleIDAllowedKey: string;
  sitePagesKey: string;
  sitePagesAllowedKey: string;
  logicKey: string;
  disableLogicToggle?: boolean;
}

// DO NOT REUSE OR COPY/PASTE. THIS IS A CUSTOM SOLUTION COMPONENT
export const AppBundleIDsSitePagesEditor = ({
  appBundleIDKey,
  appBundleIDAllowedKey,
  sitePagesKey,
  sitePagesAllowedKey,
  logicKey,
  disableLogicToggle = false,
}: IAppBundleIDsSitePagesEditorProps) => {
  const { object, defaultObject } = useFormBuilderStore();

  const [logic, setLogic] = useState(object[logicKey]);

  const handleToggleLogic = () => {
    const newLogic = logic === ELogicalOperator.AND ? ELogicalOperator.OR : ELogicalOperator.AND;
    object[logicKey] = newLogic;
    setLogic(newLogic);
  };

  useEffect(() => {
    return () => {
      object[appBundleIDKey] = defaultObject[appBundleIDKey];
      object[appBundleIDAllowedKey] = defaultObject[appBundleIDAllowedKey];
      object[sitePagesKey] = defaultObject[sitePagesKey];
      object[sitePagesAllowedKey] = defaultObject[sitePagesAllowedKey];
      object[logicKey] = defaultObject[logicKey];
    };
  }, []);

  return (
    <Rows gap={spacing.small} noPadding>
      <Row noPadding height={rowHeight}>
        <Cols gap={spacing.huge} style={{ alignItems: 'center', minHeight: `${rowHeight}px` }}>
          <Col flex>
            <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
              <Col>
                <Text small disableSelect>
                  Logic
                </Text>
              </Col>
              <Col>
                <LogicToggle
                  operator={logic}
                  handleToggleOperator={handleToggleLogic}
                  key={logicKey}
                  tooltip={logic === ELogicalOperator.OR ? OrTooltipMessage : AndTooltipMessage}
                  isDisabled={disableLogicToggle}
                />
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Row>
      <Row noPadding>
        <Rows gap={spacing.base}>
          <Row noPadding>
            <Cols>
              <Col>
                <Text
                  small
                  bold
                  style={{
                    lineHeight: rowHeight + 'px',
                    width: labelWidth + 'px',
                  }}
                >
                  App Bundle ID
                </Text>
              </Col>
              <Col flex>
                <TextInputWithInclude
                  key={appBundleIDKey}
                  placeholder={generateGenericPlaceholder('App Bundle IDs')}
                  valueKey={appBundleIDKey}
                  allowKey={appBundleIDAllowedKey}
                  tooltip={AppBundleTooltipMessage}
                  isAllowTypeBool={true}
                />
              </Col>
            </Cols>
          </Row>
          <Row noPadding>
            <Cols>
              <Col>
                <Text
                  small
                  bold
                  style={{
                    lineHeight: rowHeight + 'px',
                    width: labelWidth + 'px',
                  }}
                >
                  Site Pages
                </Text>
              </Col>
              <Col flex>
                <TextInputWithInclude
                  key={sitePagesKey}
                  placeholder={generateGenericPlaceholder('Site Pages')}
                  valueKey={sitePagesKey}
                  allowKey={sitePagesAllowedKey}
                  tooltip={SitePagesTooltipMessage}
                  isAllowTypeBool={true}
                />
              </Col>
            </Cols>
          </Row>
        </Rows>
      </Row>
    </Rows>
  );
};
